import { z } from 'zod'

import { DEFAULT_REQUIRED_ADDRESS_COMPONENTS, toMailingAddress } from '../formatting/address'

import type { FormFieldMeta, ValidationRule } from './types'
import type { FormField, FormInputType } from './generated-types'
import { isValidPhone, PHONE_VALIDATION_MESSAGE } from './phone'
import { matchesSerialRanges, ruleRegExp } from './validation-rules'

// Shown when the user typed a term into the address search box but never
// resolved it into an address (no suggestion selected, no manual entry). The
// AddressCapture stores that unresolved text as `pendingSearch` so validation
// can reject it instead of silently discarding it and saving an empty address.
export const ADDRESS_PENDING_SEARCH_MESSAGE =
  'Select an address from the results, or enter it manually.'

/**
 * Refinement shared by the required and optional ADDRESS schemas: an address
 * whose `pendingSearch` still holds unresolved search text is invalid on every
 * surface, whether or not the field itself is required. This is what stops a
 * user typing characters into the lookup box, not picking a result, and
 * "bypassing" validation with an empty (or wiped) address.
 */
function refineNoPendingAddressSearch<T extends z.ZodTypeAny>(schema: T): z.ZodEffects<T> {
  return schema.refine((value) => !hasPendingSearch(value), {
    message: ADDRESS_PENDING_SEARCH_MESSAGE,
    path: [],
  })
}

function hasPendingSearch(value: unknown): boolean {
  const pending = (value as { pendingSearch?: unknown } | null | undefined)?.pendingSearch
  return typeof pending === 'string' && pending.trim().length > 0
}

/** True when every part the field requires is filled - the manual-entry dialog's list. */
function isCompleteAddress(value: unknown, parts: readonly string[]): boolean {
  if (value == null) return false
  // Unresolved search text already has its own message.
  if (hasPendingSearch(value)) return true
  const address = toMailingAddress(value) as unknown as Record<string, string | undefined>
  return parts.every((part) => !!address[part]?.trim())
}

/**
 * Phone schema that will not reject the value the form was loaded with.
 *
 * Format validation exists to stop someone *entering* an unusable number. It
 * should not retroactively condemn a number already on the record - that only
 * blocks edits to unrelated fields on the same form, and the person editing
 * usually has no way to correct it. Pass the loaded value as `pristine` and
 * it is exempt; everything the user types is validated as normal.
 */
export function phoneSchemaPreserving(pristine: string): z.ZodTypeAny {
  if (!pristine) return InputSchemaMap.phone
  return z.string().refine(
    (value) => !value || value === pristine || isValidPhone(value),
    { message: PHONE_VALIDATION_MESSAGE },
  )
}

/**
 * Base Zod schema per form-input type. Bases are permissive/optional;
 * `getFormValidation` layers `required` and `applyCustomAttributeRules`
 * layers backend-defined rules on top.
 */
export const InputSchemaMap: Record<FormInputType, z.ZodTypeAny> = {
  text: z.string(),
  email: z.string(),
  password: z.string(),
  checkbox: z.boolean(),
  switch: z.boolean(),
  select: z.string(),
  multiSelect: z.array(z.string()),
  datepicker: z.string(),
  phone: z
    .string()
    .refine((value) => !value || isValidPhone(value), { message: PHONE_VALIDATION_MESSAGE }),
  picture: z.any(),
  dateRange: z
    .object({ from: z.string().optional(), to: z.string().optional() })
    .partial(),
  number: z.union([z.number(), z.string()]),
  incremental: z.array(z.any()),
  currency: z
    .object({ amount: z.number().optional(), currency: z.string().optional() })
    .partial(),
  'rich-text': z.string().refine(
    (value) => {
      if (!value) return true
      if (typeof document === 'undefined') return value.trim() !== ''
      const div = document.createElement('div')
      div.innerHTML = value
      return (div.textContent ?? '').trim() !== ''
    },
    { message: 'Please enter a value' },
  ),
  file: z.any(),
  custom: z.any(),
  disclaimer: z.boolean(),
  address: refineNoPendingAddressSearch(
    z
      .object({
        addressLine1: z.string().optional(),
        addressLine2: z.string().optional(),
        locality: z.string().optional(),
        region: z.string().optional(),
        postalCode: z.string().optional(),
        countryCode: z.string().optional(),
        pendingSearch: z.string().optional(),
      })
      .partial()
      // An address nobody filled in opens on null (seedObjectValuedValues).
      .nullable()
      .optional(),
  ),
  'entity-search': z
    .object({ uuid: z.string().optional(), name: z.string().optional() })
    .nullable(),
  'search-autocomplete': z.any(),
  textarea: z.string(),
}

/**
 * A `BOOLEAN` attribute renders one of three ways, matching the convention in
 * `metadata-field.tsx`:
 *   - `configuration_data.display_as === 'yes_no'` → a Yes/No select, where
 *     "No" is a legitimate answer that still satisfies `required` (e.g. the
 *     Owens Corning "Was a <component> used?" registration fields).
 *   - `configuration_data.display_as === 'disclaimer'` → a disclaimer panel
 *     (title + long copy from `configuration_data.disclaimer_title` /
 *     `disclaimer_text`) with the acknowledgement checkbox rendered as the
 *     final line, its label being the attribute's `display_name` (e.g. the
 *     Teknor cut-hose liability notice + "both ends are cut" confirmation).
 *     Validates exactly like a checkbox: when `required` it must be checked.
 *   - otherwise → a real checkbox. When `required`, the checkbox must be
 *     checked to pass validation (an acknowledgement / consent box).
 */
export function resolveBooleanInputType(field: {
  configuration_data?: Record<string, unknown> | null
}): FormInputType {
  const displayAs = field.configuration_data?.['display_as']
  if (displayAs === 'yes_no') return 'select'
  if (displayAs === 'disclaimer') return 'disclaimer'
  return 'checkbox'
}

/**
 * Whether a *required* field's current value counts as "the user provided an
 * answer". This is the single source of truth for the hand-rolled claim-intake
 * surfaces that render fields through `MetadataField` (the multi-item editor,
 * the admin single-claim wizard, the child/bulk claim dialogs) - none of which
 * run the generated Zod schema.
 *
 * The subtle case, re-broken repeatedly (DL-44 / CLM-017 / MH-017): a required
 * BOOLEAN rendered as a checkbox (an acknowledgement / consent box, e.g. the
 * Teknor "both ends are cut" confirmation) is only satisfied when it is
 * *checked* (`=== true`). `false` - including the check-then-uncheck path -
 * must NOT count as answered, because a naive `String(false).trim() !== ''`
 * emptiness test lets an unchecked box through. A BOOLEAN rendered as a Yes/No
 * control (`display_as: 'yes_no'`) treats an explicit "No" (`false`) as a valid
 * answer and falls through to the generic non-empty check.
 *
 * Mirrors the `case 'checkbox'` refine (`v === true`) in `getFormValidation`
 * so the generated `useForm` path and the `MetadataField` paths agree.
 */
export function isRequiredValueProvided(
  field: {
    field_type?: string
    configuration_data?: Record<string, unknown> | null
  },
  value: unknown,
): boolean {
  if (field.field_type === 'BOOLEAN') {
    const booleanInput = resolveBooleanInputType(field)
    if (booleanInput === 'checkbox' || booleanInput === 'disclaimer') {
      return value === true
    }
  }
  if (value === undefined || value === null || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') {
    const type = field.field_type?.toUpperCase()
    if (type === 'MONEY' || type === 'CURRENCY') {
      // MONEY / CURRENCY persists `{ currency }` with no `amount` until entered;
      // an empty amount is not an answer (0 is a legitimate amount).
      const amount = (value as { amount?: unknown }).amount
      return amount !== undefined && amount !== null && amount !== ''
    }
    // Other object-valued fields (ADDRESS, ENTITY_SEARCH, ...) persist as
    // objects (e.g. `{ uuid, name }`); a present object counts as provided.
    // The fine-grained shape is validated by the generated Zod schema.
    return true
  }
  return String(value).trim() !== ''
}

/**
 * Coerce a checkbox/disclaimer seed to a real boolean (SX-218). Callers seed an
 * unanswered attribute as `''`, and a draft or EAV row can hold `"true"`, both
 * of which the `z.boolean().nullable().optional()` schema rejects on submit.
 * Anything not affirmative is `false`, matching the unchecked state.
 */
export function normaliseCheckboxValue(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    return ['true', '1', 'yes', 'on'].includes(value.trim().toLowerCase())
  }
  return false
}

export const FieldTypeMap: Record<string, FormInputType> = {
  BOOLEAN: 'select',
  DATE: 'datepicker',
  DATE_TIME: 'datepicker',
  RICH_TEXT: 'rich-text',
  LONG_TEXT: 'textarea',
  TEXTAREA: 'textarea',
  FILE: 'file',
  DOCUMENT: 'file',
  IMAGE: 'picture',
  LINK: 'text',
  MONEY: 'currency',
  CURRENCY: 'currency',
  MULTI_SELECT: 'select',
  NUMBER: 'number',
  DECIMAL: 'number',
  SELECT: 'select',
  DROPDOWN: 'select',
  TEXT: 'text',
  VIDEO: 'file',
  PHONE: 'phone',
  EMAIL: 'email',
  ADDRESS: 'address',
  ENTITY_SEARCH: 'entity-search',
}

const SELECT_LIKE_TYPES: FormInputType[] = [
  'select',
  'multiSelect',
  'checkbox',
  'file',
  'picture',
  'datepicker',
  'dateRange',
]

export function getRequiredMessage(field: {
  field_type: string
  display_name: string
  configuration_data?: Record<string, unknown> | null
}): string {
  const label = field.display_name.toLowerCase()
  const formInputType =
    field.field_type === 'BOOLEAN'
      ? resolveBooleanInputType(field)
      : (FieldTypeMap[field.field_type] ?? 'text')
  if (formInputType === 'checkbox' || formInputType === 'disclaimer') {
    return 'You must confirm this to continue'
  }
  return SELECT_LIKE_TYPES.includes(formInputType)
    ? `Please select ${label}`
    : `Please enter ${label}`
}

export function applyCustomAttributeRules(
  baseSchema: z.ZodTypeAny,
  rules: ValidationRule[] = [],
  fieldType: FormInputType,
): z.ZodTypeAny {
  let schema: z.ZodTypeAny = baseSchema ?? InputSchemaMap[fieldType]

  for (const rule of rules) {
    const message = rule.failure_message || undefined

    // Length/pattern rules only apply to string schemas. Layer them via a
    // refine so they compose regardless of any wrapping (optional, etc.).
    switch (rule.validation_type) {
      case 'min_length': {
        const min = Number(rule.min_length)
        schema = schema.refine((v) => typeof v !== 'string' || v.length >= min, {
          message: message ?? `Must be at least ${min} characters`,
        })
        break
      }
      case 'max_length': {
        const max = Number(rule.max_length)
        schema = schema.refine((v) => typeof v !== 'string' || v.length <= max, {
          message: message ?? `Must be at most ${max} characters`,
        })
        break
      }
      case 'regex':
      case 'pattern': {
        const re = ruleRegExp(rule)
        // An unparseable pattern is still enforced server-side; don't break the form.
        if (re) {
          schema = schema.refine((v) => typeof v !== 'string' || v === '' || re.test(v), {
            message: message ?? 'Invalid format',
          })
        }
        break
      }
      case 'serial_range': {
        const ranges = rule.ranges
        schema = schema.refine(
          (v) => {
            if (typeof v === 'string') {
              return v === '' || matchesSerialRanges(v, ranges)
            }
            if (Array.isArray(v)) {
              return v.length === 0 || matchesSerialRanges(v, ranges)
            }
            return true
          },
          { message: message ?? 'Invalid format' },
        )
        break
      }
    }
  }

  return schema
}

export function genFormFields(field: FormFieldMeta): FormField {
  const baseType =
    field.field_type === 'BOOLEAN'
      ? resolveBooleanInputType(field)
      : (FieldTypeMap[field.field_type] ?? 'text')

  // A TEXT field that declares `configuration_data.autocomplete_source`
  // renders as a type-ahead over prior values (WPS-164 #15: Plant /
  // Production Code). Generic: any such attribute opts in via config, no
  // field name is hardcoded.
  const fieldType =
    baseType === 'text' &&
    typeof field.configuration_data?.autocomplete_source === 'string' &&
    field.configuration_data.autocomplete_source
      ? 'search-autocomplete'
      : baseType

  return {
    type: fieldType,
    name: field.code_name,
    label: field.display_name,
    placeholder: field.display_name,
    required: field.required ? getRequiredMessage(field) : undefined,
    options:
      field.field_type === 'BOOLEAN'
        ? fieldType === 'select'
          ? [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]
          : undefined
        : field.options?.map((option) => ({
            label: option.label,
            value: option.value,
            id: option.value,
            // Carry the per-option severity through so checklist-style
            // multi-selects can render finding chips. Undefined for
            // standard options.
            severity: option.severity,
          })),
    className:
      fieldType === 'rich-text' ||
      fieldType === 'picture' ||
      fieldType === 'textarea' ||
      fieldType === 'checkbox' ||
      fieldType === 'disclaimer'
        ? 'col-span-2'
        : '',
    isMulti: field.field_type === 'MULTI_SELECT',
    hint: field.hint,
    useCase: field.file_use_case,
    multiple: field.file_multiple,
    configurationData: field.configuration_data,
  }
}

function getGeneratedFieldName(field: FormField): string {
  if ('name' in field && field.name) return field.name
  if ('label' in field && field.label) return field.label.replace(/ /g, '_').toLowerCase()
  throw new Error("FormField must have either a 'name' or 'label' property")
}

const requiredMessage = (field: FormField): string =>
  typeof field.required === 'string' ? field.required : 'This field is required'

/**
 * Build a Zod schema for a single generated field. Bases come from
 * `InputSchemaMap`; `required` adds the appropriate non-empty constraint
 * per field shape, and non-required fields are made optional so empty
 * values pass on submit.
 */
export const getFormValidation = (field: FormField): z.ZodTypeAny => {
  const msg = requiredMessage(field)
  const required = Boolean(field.required)

  switch (field.type) {
    case 'select':
    case 'multiSelect':
      if (field.isMulti || field.type === 'multiSelect') {
        const arr = z.array(z.string())
        return required ? arr.min(1, { message: msg }) : arr.optional()
      }
      return required ? z.string().min(1, { message: msg }) : z.string().optional()

    case 'checkbox':
    case 'switch':
    case 'disclaimer':
      // A required checkbox (or disclaimer acknowledgement) must be checked to
      // pass. `.optional()` first so an untouched (undefined) value reaches the
      // refine as a failing value rather than a raw type error; this is also
      // what closes the check-then-uncheck hole, since `false` fails the
      // `=== true` refine.
      return required
        ? z.boolean().nullable().optional().refine((v) => v === true, { message: msg })
        : z.boolean().nullable().optional()

    case 'email': {
      const email = z.string().email({ message: 'Please enter a valid email' })
      if (required) return z.string().min(1, { message: msg }).email({ message: 'Please enter a valid email' })
      return z.union([z.literal(''), email]).optional()
    }

    case 'datepicker':
      return required ? z.string().min(1, { message: msg }) : z.string().optional()

    case 'number': {
      const numericString = z.string().refine((val) => !val || !isNaN(Number(val)), {
        message: 'Must be a valid number',
      })
      if (required) {
        return z.union([
          z.number(),
          z.string().min(1, { message: msg }).refine((val) => !isNaN(Number(val)), {
            message: 'Must be a valid number',
          }),
        ])
      }
      return z.union([z.number(), numericString]).optional()
    }

    case 'currency':
      if (required) {
        return z.object({
          amount: z.number({ message: msg }),
          currency: z.string().optional(),
        })
      }
      return z
        .object({ amount: z.number().optional(), currency: z.string().optional() })
        .partial()

    case 'phone': {
      // A stored number that predates E.164, or belongs to a country other
      // than the org default, cannot be re-parsed against the country the
      // selector defaults to - a UK national number like "07889362951" is
      // simply not a valid US number, and nothing can infer its country
      // because it carries no dialling code. Validating it on load would
      // block the whole form: someone editing a customer's surname would
      // first have to repair phone data they never touched.
      //
      // So the value the form loaded with always passes. Only a number the
      // user actually typed is validated - which is the point of the check.
      // Required still means required; this exempts the format rule, not the
      // presence one.
      const base = phoneSchemaPreserving(
        typeof field.value === 'string' ? field.value : '',
      )
      return required ? z.string().min(1, { message: msg }).and(base) : base.optional()
    }

    case 'incremental': {
      if (field.fields) {
        const shape = field.fields.reduce<Record<string, z.ZodTypeAny>>((acc, subField) => {
          acc[getGeneratedFieldName(subField)] = getFormValidation(subField)
          return acc
        }, {})
        const arr = z.array(z.object(shape).passthrough())
        return required ? arr.min(1, { message: msg }) : arr.optional()
      }
      return z.array(z.any()).optional()
    }

    case 'address':
      if (required) {
        // One issue on the field itself: AddressField shows only the field's
        // own message, so per-part issues blocked submit with nothing shown.
        const configured = field.configurationData?.required_components
        const parts =
          Array.isArray(configured) && configured.length > 0
            ? (configured as string[])
            : DEFAULT_REQUIRED_ADDRESS_COMPONENTS
        return InputSchemaMap.address.refine((v) => isCompleteAddress(v, parts), {
          message: msg,
          path: [],
        })
      }
      return InputSchemaMap.address

    case 'entity-search':
      if (required) {
        return z
          .object({ uuid: z.string(), name: z.string() })
          .nullable()
          .refine((v) => v != null, { message: msg })
      }
      return InputSchemaMap['entity-search']

    case 'file':
    case 'picture':
      if (!required) return z.any().optional()
      // Multi-file fields always hold an array of upload envelopes, so require a
      // non-empty array - an emptied list (`[]`) or a stray non-array value must
      // fail "required". Single-file fields hold a bare envelope/UUID, caught by
      // the null/'' check.
      return field.multiple
        ? z
            .any()
            .refine((v) => Array.isArray(v) && v.length > 0, { message: msg })
        : z.any().refine((v) => v != null && v !== '', { message: msg })

    case 'rich-text':
      return required
        ? (InputSchemaMap['rich-text'] as z.ZodString).min(1, { message: msg })
        : InputSchemaMap['rich-text'].optional()

    case 'dateRange':
      return InputSchemaMap.dateRange.optional()

    case 'text':
    case 'textarea':
    case 'password':
      return required ? z.string().min(1, { message: msg }) : z.string().optional()

    default:
      // search-autocomplete, custom → permissive base (`z.any()`)
      return (InputSchemaMap[field.type] ?? z.string()).optional()
  }
}
